import { useRef } from "react";
import { expandClaims, getChildModules,postModuleElements } from "../../services/apis";

export const useEditorModuleClaims = ({
  appData,
  allMustUseDataEmpty,
  setOptionalModules,
  setMustUseDataAvailable,
  setmoduleSymbol,
  setModuleStatuses,
  setLoadingModules,
}) => {
  const textSymbolMapRef = useRef(new Map());

  const initializeTexts = (texts) => {
    for (const text of texts) {
      if (!textSymbolMapRef.current.has(text)) {
        textSymbolMapRef.current.set(text, "Not Assigned");
      }
    }
  };

  const expandClaimsPromise = (moduleTypeID) => {
    return new Promise((resolve, reject) => {
      expandClaims(moduleTypeID, (response, err, errorCode) => {
        if (err) {
          reject({ errorCode, err });
        } else {
          let componentClaims = response?.data?.components?.map(
            (item) => item?.relatedClaims,
          );

          const combinedClaims = [
            ...(response?.data?.claims || []),
            ...(componentClaims || []),
          ];

          resolve({
            claims: combinedClaims,
            data: response?.data,
          });
        }
      });
    });
  };

  const checkAllMustUseData = (mustUseData) => {
    mustUseData.forEach((module) => {
      if (module.mlrStatus === "Available for Use") {
        module.claims.forEach((claim) => {
          if (claim.mlrStatus === "Available for Use" && claim.mustUse) {
            allMustUseDataEmpty.current = false;
            return;
          }
        });
        module.reusableTexts.forEach((reusableText) => {
          if (
            reusableText.mlrStatus === "Available for Use" &&
            reusableText.mustUse
          ) {
            allMustUseDataEmpty.current = false;
            return;
          }
        });
        module.components.forEach((component) => {
          if (
            component.mlrStatus === "Available for Use" &&
            component.mustUse
          ) {
            allMustUseDataEmpty.current = false;
            return;
          }
        });
      }
    });
  };
  const saveModuleData = (payload) => {
    return new Promise((resolve, reject) => {
      postModuleElements(payload, (response, err, errorCode) => {
        if (err) {
          reject({ err, errorCode });
        } else {
          resolve(response);
        }
      });
    });
  };
  const populateModuleData = (allData) => {
    let saveData = [];
    const appendUsedContentItem = (type, veevaId, veevaDocNumber) => {
      const normalizedVeevaId = String(veevaId || "").trim();
      const normalizedDocNumber = String(veevaDocNumber || "").trim();
      if (!normalizedVeevaId || !normalizedDocNumber) return;
      saveData.push({
        type,
        veevaId: normalizedVeevaId,
        veevaDocNumber: normalizedDocNumber,
      });
    };

    allData.forEach((data) => {
      if (data.mlrStatus !== "Expired") {
        appendUsedContentItem("Module", data.moduleDamId, data.moduleDamName);
      }
      data.claims.forEach((claim) => {
        if (claim.mlrStatus !== "Expired") {
          appendUsedContentItem("Claim", claim.claimDamId, claim.claimName);
        }
      });
      data.components.forEach((component) => {
        if (component.mlrStatus !== "Expired") {
          appendUsedContentItem(
            "Component",
            component.componentDamId,
            component.componentDocNumber,
          );
        }
      });
      data.reusableTexts.forEach((rt) => {
        if (rt.mlrStatus !== "Expired") {
          appendUsedContentItem(
            "ReusableText",
            rt.reusableTextDamId,
            rt.reusableTextName,
          );
        }
      });
    });
    const payload = { assetId: appData?.assetInfo?.assetId, items: saveData };
    if (!payload.assetId || payload.items.length === 0) {
      return;
    }
    saveModuleData(payload).catch(() => {
      // keep behavior: non-blocking metadata sync for editor load
    });
  };

  const fetchAllClaims = async (moduleTypeIDs, apiModules) => {
    try {
      const promises = moduleTypeIDs.map((id) => expandClaimsPromise(id));
      const settledResults = await Promise.allSettled(promises);
     
      const failedPromises = settledResults.filter(
        (result) => result.status === "rejected",
      );
     
      if (failedPromises.length > 0) {
        console.error("Some modules failed to load:", failedPromises);
      }
 
      const completeData = settledResults
      .filter((result) => result.status === "fulfilled")
      .map((result) => result.value);
 
      if (completeData.length === 0) {
        setLoadingModules(false);
        return;
      }
 
      let allData = completeData.map((r) => r.data);
      populateModuleData(allData);
      let results = completeData.map((r) => r.claims).flat(Infinity);
      results = results.concat(
        allData.map((item) => item.components).flat(Infinity),
      );
      const optionalModuleNotUsed = allData.flatMap((module) =>
        module.businessRules
          .filter((rule) => rule.ruleCondition === "Optional")
          .flatMap((rule) =>
            rule.targetModules.map((t) => t.targetModuleDamName),
          ),
      );
      const notMustUsedModule = allData.flatMap((module) =>
        module.businessRules
          .filter((rule) => rule.ruleCondition !== "MustExistWith")
          .flatMap((rule) =>
            rule.targetModules.map((t) => t.targetModuleDamName),
          ),
      );
      const mustUseData = allData.filter(
        (module) =>
          !notMustUsedModule.some((name) => name === module.moduleDamName),
      );
 
      const filteredOptionalModules = allData.filter((module) =>
        optionalModuleNotUsed.some((name) => name === module.moduleDamName),
      );
 
      setOptionalModules(filteredOptionalModules);
      setMustUseDataAvailable(mustUseData);
      checkAllMustUseData(mustUseData);
      results = results.flatMap((i) => {
        const list = [];
 
        if (typeof i.footnotes === "string") {
          list.push(
            ...i.footnotes
              .split(".,")
              .map((s) => s.trim())
              .filter(Boolean)
              .map((s) => (s.endsWith(".") ? s : s + ".")),
          );
        }
 
        if (Array.isArray(i.relatedClaims)) {
          i.relatedClaims.forEach((rcItem) => {
            if (typeof rcItem.footnotes === "string") {
              rcItem.footnotes
                .split(".,")
                .map((s) => s.trim())
                .filter(Boolean)
                .forEach((s) => {
                  list.push(s.endsWith(".") ? s : s + ".");
                });
            }
          });
          i.relatedClaims.forEach((mtItem) => {
            if (
              typeof mtItem.matchText === "string" &&
              i.subType != "Data Graphic"
            ) {
              mtItem.matchText
                .split(".,")
                .map((s) => s.trim())
                .filter(Boolean)
                .forEach((s) => {
                  list.push(s.endsWith(".") ? s : s + ".");
                });
            }
          });
        }
        return list;
      });
 
      if (apiModules) {
        const storedSymbols = localStorage.getItem("allSymbols") || "[]";
        const parsed = JSON.parse(storedSymbols);
        textSymbolMapRef.current = new Map(parsed);
      }
      for (const key of textSymbolMapRef.current.keys()) {
        if (!results.includes(key)) {
          textSymbolMapRef.current.delete(key);
        }
      }
      initializeTexts(results);
      localStorage.setItem(
        "allSymbols",
        JSON.stringify(Array.from(textSymbolMapRef.current.entries())),
      );
      setmoduleSymbol(Array.from(textSymbolMapRef.current.entries()));
      const ModulesStatusesArray = allData.map((m) => ({
        moduleDamName: m.moduleDamName,
        mlrStatus: m.mlrStatus,
      }));
      setModuleStatuses(ModulesStatusesArray);
    } catch (error) {
      console.error("Unexpected error while fetching all claims:", error);
    } finally{
      setLoadingModules(false);
    }
  };

  const handleGetChildModules = (moduleId) => {
    return new Promise((resolve, reject) => {
      getChildModules(moduleId, (response, err, errorCode) => {
        if (err) {
          reject({ moduleId, err, errorCode });
        } else {
          resolve(response?.data);
        }
      });
    });
  };

  const fetchAllChildModules = async (moduleIds) => {
    const allTargetModuleIds = [];
    try {
      const promises = moduleIds.map((id) => handleGetChildModules(id));
      const data = await Promise.all(promises);

      data.forEach((module) => {
        if (!Array.isArray(module.businessRules)) return;

        module.businessRules.forEach((rule) => {
          const condition = rule?.ruleCondition?.toLowerCase();
          if (condition === "optional" || condition === "mustexistwith") {
            if (!Array.isArray(rule.targetModules)) return;

            rule.targetModules.forEach((target) => {
              if (
                target?.targetModuleId &&
                target?.targetModuleMlrStatus?.toLowerCase() ===
                  "available for use"
              ) {
                allTargetModuleIds.push(target.targetModuleId);
              }
            });
          }
        });
      });
    } catch (error) {
      console.error("Error fetching one or more child modules:", error);
    }

    return allTargetModuleIds;
  };

  return {
    checkAllMustUseData,
    fetchAllClaims,
    fetchAllChildModules,
  };
};
